import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { FilterCompleteService } from "src/app/filter-complete/filter-complete";
import { Entity, Plan } from "src/app/model/entity";
import { EntityService } from "src/app/services/entity.service";
import { IntegratedAdmissionWizardService } from "../../service/integrated-admission-wizard.service";
import { AliquotTaxService } from "src/app/services/aliquot-tax/aliquot-tax.service";
import { AliquotTax } from "src/app/model/aliquot-tax";
import { IntegratedAdmissionAppointmentPlan } from "src/app/model/integrated-admission/integrated-admission-appointment-plan";
import { Subscription } from "rxjs";
import { NormativeDialogComponent } from "src/app/components/dialogs/normative/normative-dialog.component";
import { MatDialog } from "@angular/material";
import { Normative } from "src/app/model/normative";

@Component({
  selector: "app-integrated-admission-appointment-plan",
  templateUrl: "./integrated-admission-appointment-plan.component.html",
  styleUrls: ["./integrated-admission-appointment-plan.component.css"],
})
export class IntegratedAdmissionAppointmentPlanComponent
  implements OnInit, OnDestroy
{
  form: FormGroup;
  readOnly: boolean = false;
  entities: Entity[] = [];
  plans: Plan[] = [];
  aliquotTaxs: AliquotTax[] = [];
  filterCompleteEntity: FilterCompleteService<Entity>;
  filterCompletePlan: FilterCompleteService<Plan>;
  filterCompleteAliquotTax: FilterCompleteService<AliquotTax>;
  private isPatchingFromAppointment = false;
  currentAdmissionAppointmentSubscription: Subscription;
  readonlySubscription: Subscription;

  normativeSubscription: Subscription;
  normative: Normative;

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private entityService: EntityService,
    private alicuotTaxService: AliquotTaxService,
    private service: IntegratedAdmissionWizardService,
  ) {
    this.form = this.formBuilder.group({
      Entity: [null],
      Plan: [null],
      AliquotTax: [null],
    });

    this.initFilters();
    this.subscribeEvents();
  }

  get filterCompleteAliquotTaxOptions() {
    return this.filterCompleteAliquotTax
      ? this.filterCompleteAliquotTax.filteredOptions
      : [];
  }

  get filterCompleteEntityOptions() {
    return this.filterCompleteEntity
      ? this.filterCompleteEntity.filteredOptions
      : [];
  }

  get filterCompletePlanOptions() {
    return this.filterCompletePlan
      ? this.filterCompletePlan.filteredOptions
      : [];
  }

  get formIsValid() {
    return this.form.valid;
  }

  async ngOnInit() {}

  showNormative(): void {
    this.dialog.open(NormativeDialogComponent, {
      width: "70%",
      data: this.normative,
    });
  }

  private subscribeEvents() {
    this.currentAdmissionAppointmentSubscription =
      this.service.currentAdmissionAppointment$.subscribe(
        async (appointment) => {
          this.isPatchingFromAppointment = true;

          if (!this.readOnly) {
            this.readOnly = appointment.ExistsCurrentInvoice;
          }

          // reaad only if is true

          this.form.patchValue(
            {
              Entity: null,
              Plan: null,
              AliquotTax: null,
            },
            { emitEvent: false },
          );

          await this.loadHealthInsurance(
            appointment.Plan.Entity ? appointment.Plan.Entity.Id : null,
          );
          if (appointment.Plan.Plan && appointment.Plan.Entity) {
            await this.loadInsurancePlan(
              appointment.Plan.Entity,
              appointment.Plan.Plan.Id,
            );

            if (appointment.Plan && appointment.Plan.Plan) {
              await this.loadAliquotTax(
                appointment.Plan.Plan,
                appointment.Plan.AliquotTax
                  ? appointment.Plan.AliquotTax.Id
                  : null,
              );
            }
          }

          this.isPatchingFromAppointment = false;
        },
      );

    this.normativeSubscription =
      this.service.currentNormativeSubject$.subscribe((normative) => {
        this.normative = normative;
      });

    this.readonlySubscription = this.service.currentReadOnlySubject$.subscribe(
      (readOnly) => {
        if (readOnly) {
          this.readOnly = readOnly;
          if (this.readOnly) {
            this.form.disable();
          }
        }
      },
    );

    this.form.controls.Entity.valueChanges.subscribe(async (value) => {
      this.plans = [];
      this.aliquotTaxs = [];
      this.initPlanFilter();
      this.initAliquotTaxFilter();

      this.form.patchValue(
        {
          Plan: null,
          AliquotTax: null,
        },
        { emitEvent: false },
      );

      if (!value || typeof value === "string") {
        return;
      } else {
        await this.loadInsurancePlan(value);
      }
    });

    this.form.controls.Plan.valueChanges.subscribe(async (value) => {
      this.aliquotTaxs = [];
      this.form.patchValue(
        {
          AliquotTax: null,
        },
        { emitEvent: false },
      );

      this.initAliquotTaxFilter();

      if (!value || typeof value === "string") {
        return;
      }
      await this.loadAliquotTax(value);
    });

    this.form.valueChanges.subscribe((value) => {
      if (this.isPatchingFromAppointment) {
        return;
      }

      const plan = new IntegratedAdmissionAppointmentPlan();
      plan.AliquotTax =
        value.AliquotTax && typeof value.AliquotTax !== "string"
          ? value.AliquotTax
          : null;
      plan.Plan =
        value.Plan && typeof value.Plan !== "string" ? value.Plan : null;
      plan.Entity =
        value.Entity && typeof value.Entity !== "string" ? value.Entity : null;

      this.service.setPlan(plan);
    });
  }

  private async loadHealthInsurance(entityId: number = null) {
    this.entities =
      this.entities.length > 0
        ? this.entities
        : await this.entityService.getEntities(true);

    this.filterCompleteEntity = new FilterCompleteService<Entity>(
      this.entities,
      this.form.controls.Entity,
    );

    if (entityId) {
      const entityIndex = this.entities.findIndex((x) => x.Id === entityId);
      this.form.patchValue(
        {
          Entity: this.entities[entityIndex],
        },
        { emitEvent: false },
      );
    }
  }

  private async loadInsurancePlan(entity: Entity, planId: number = null) {
    this.plans = await this.entityService.getPlansByEntityId(entity.Id);
    this.filterCompletePlan = new FilterCompleteService<Plan>(
      this.plans,
      this.form.controls.Plan,
    );

    if (planId) {
      const planIndex = this.plans.findIndex((x) => x.Id === planId);
      this.form.patchValue(
        {
          Plan: this.plans[planIndex],
        },
        { emitEvent: false },
      );
    }
  }

  private async loadAliquotTax(plan: Plan, aliquotTaxId: number = null) {
    this.aliquotTaxs = await this.alicuotTaxService.getAliquotsTaxByPlanId(
      plan.Id,
    );
    this.filterCompleteAliquotTax = new FilterCompleteService<AliquotTax>(
      this.aliquotTaxs,
      this.form.controls.AliquotTax,
    );

    if (!aliquotTaxId && this.aliquotTaxs.length === 1) {
      aliquotTaxId = this.aliquotTaxs[0].Id;
    }

    if (aliquotTaxId) {
      const aliquotTaxIndex = this.aliquotTaxs.findIndex(
        (x) => x.Id === aliquotTaxId,
      );
      this.form.patchValue({
        AliquotTax: this.aliquotTaxs[aliquotTaxIndex],
      });
    }
  }

  private initPlanFilter() {
    this.filterCompletePlan = new FilterCompleteService<Plan>(
      this.plans,
      this.form.controls.Plan,
    );
  }

  private initAliquotTaxFilter() {
    this.filterCompleteAliquotTax = new FilterCompleteService<AliquotTax>(
      this.aliquotTaxs,
      this.form.controls.AliquotTax,
    );
  }

  private initFilters() {
    this.filterCompleteEntity = new FilterCompleteService<Entity>(
      this.entities,
      this.form.controls.Entity,
    );

    this.initPlanFilter();

    this.initAliquotTaxFilter();
  }

  ngOnDestroy(): void {
    if (this.currentAdmissionAppointmentSubscription) {
      this.currentAdmissionAppointmentSubscription.unsubscribe();
    }

    if (this.normativeSubscription) {
      this.normativeSubscription.unsubscribe();
    }

    if (this.readonlySubscription) {
      this.readonlySubscription.unsubscribe();
    }
  }

  revalorizate() {
    this.service.revalorizate(true);
  }
}
